import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Box,
    Typography,
    IconButton,
    Divider,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import moment from 'moment-timezone'
import {
    InvoiceUpdateRequest,
    InvoiceResponse,
    InvoiceType,
    InvoiceStatus,
    PaymentTerms,
    InvoiceLineItem,
    clientInvoicesApi,
} from '@assembly/api/clientInvoices'

interface Props {
    open: boolean
    invoice: InvoiceResponse | null
    onClose: () => void
    onSuccess: () => void
}

interface FormData {
    organizationId: string
    assemblyLineId: string
    type: InvoiceType
    status: InvoiceStatus
    paymentTerms: PaymentTerms
    issueDate: moment.Moment | null
    billingPeriodStart: moment.Moment | null
    billingPeriodEnd: moment.Moment | null
    description: string
    notes: string
    currency: string
    stripeInvoiceId: string
    stripeCustomerId: string
    externalReference: string
    lineItems: InvoiceLineItem[]
    customTaxAmount: number
    customTotalAmount: number
}

const defaultLineItem: InvoiceLineItem = {
    description: '',
    quantity: 1,
    unit: 'hours',
    unitPrice: 0,
    totalAmount: 0,
    type: 'CONTRACTOR_HOURS',
}

export default function EditInvoiceDialog({ open, invoice, onClose, onSuccess }: Props) {
    const { addAlert } = useSnackbars()
    const [loading, setLoading] = useState(false)
    const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([])
    const [customTaxAmount, setCustomTaxAmount] = useState<number>(0)
    const [customTotalAmount, setCustomTotalAmount] = useState<number>(0)
    const [isCustomTax, setIsCustomTax] = useState(false)
    const [isCustomTotal, setIsCustomTotal] = useState(false)

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues: {
            organizationId: '',
            assemblyLineId: '',
            type: 'MONTHLY',
            status: 'DRAFT',
            paymentTerms: 'NET_30',
            issueDate: moment(),
            billingPeriodStart: moment(),
            billingPeriodEnd: moment(),
            description: '',
            notes: '',
            currency: 'USD',
            stripeInvoiceId: '',
            stripeCustomerId: '',
            externalReference: '',
            lineItems: [],
            customTaxAmount: 0,
            customTotalAmount: 0,
        },
        mode: 'onChange',
    })

    useEffect(() => {
        if (open && invoice) {
            // Normalize subscription line items - ensure unit is MONTHLY or ANNUAL
            const normalizedLineItems = invoice.lineItems.length > 0 
                ? invoice.lineItems.map(item => {
                    if (item.type === 'SUBSCRIPTION') {
                        return {
                            ...item,
                            quantity: 1, // Ensure quantity is 1 for subscriptions
                            unit: (item.unit === 'MONTHLY' || item.unit === 'ANNUAL') ? item.unit : 'MONTHLY',
                            totalAmount: item.unitPrice || 0, // Recalculate total for subscriptions
                        }
                    }
                    return item
                })
                : [{ ...defaultLineItem }]

            const subtotal = calculateSubtotalFromItems(normalizedLineItems)
            const calculatedTax = subtotal * 0.08 // 8% tax rate
            const actualTax = invoice.taxAmount || 0
            const actualTotal = invoice.totalAmount || 0
            const calculatedTotal = subtotal + calculatedTax

            setCustomTaxAmount(actualTax)
            setCustomTotalAmount(actualTotal)
            setIsCustomTax(Math.abs(actualTax - calculatedTax) > 0.01)
            setIsCustomTotal(Math.abs(actualTotal - calculatedTotal) > 0.01)

            reset({
                organizationId: invoice.organization?.id || '',
                assemblyLineId: invoice.assemblyLine?.id || '',
                type: invoice.type,
                status: invoice.status,
                paymentTerms: invoice.paymentTerms,
                issueDate: invoice.issueDate ? moment(invoice.issueDate) : moment(),
                billingPeriodStart: invoice.billingPeriodStart ? moment(invoice.billingPeriodStart, 'YYYY-MM-DD') : moment(),
                billingPeriodEnd: invoice.billingPeriodEnd ? moment(invoice.billingPeriodEnd, 'YYYY-MM-DD') : moment(),
                description: invoice.description || '',
                notes: invoice.notes || '',
                currency: invoice.currency || 'USD',
                stripeInvoiceId: invoice.stripeInvoiceId || '',
                stripeCustomerId: invoice.stripeCustomerId || '',
                externalReference: invoice.externalReference || '',
                lineItems: normalizedLineItems,
                customTaxAmount: actualTax,
                customTotalAmount: actualTotal,
            })
            setLineItems(normalizedLineItems)
        }
    }, [open, invoice, reset])

    const handleAddLineItem = () => {
        setLineItems([...lineItems, { ...defaultLineItem }])
    }

    const handleRemoveLineItem = (index: number) => {
        if (lineItems.length > 1) {
            const newLineItems = lineItems.filter((_, i) => i !== index)
            setLineItems(newLineItems)
            setValue('lineItems', newLineItems)
        }
    }

    const handleLineItemChange = (index: number, field: keyof InvoiceLineItem, value: any) => {
        const newLineItems = [...lineItems]
        newLineItems[index] = { ...newLineItems[index], [field]: value }

        // If changing type to SUBSCRIPTION, set quantity to 1 and unit to MONTHLY
        if (field === 'type' && value === 'SUBSCRIPTION') {
            newLineItems[index].quantity = 1
            if (!newLineItems[index].unit || (newLineItems[index].unit !== 'MONTHLY' && newLineItems[index].unit !== 'ANNUAL')) {
                newLineItems[index].unit = 'MONTHLY'
            }
        }

        // Calculate total amount
        if (field === 'quantity' || field === 'unitPrice' || field === 'type') {
            const item = newLineItems[index]
            if (item.type === 'SUBSCRIPTION') {
                // For subscriptions, total equals unit price (quantity is always 1)
                newLineItems[index].totalAmount = item.unitPrice || 0
            } else {
                // For other types, calculate normally
                const quantity = field === 'quantity' ? value : item.quantity
                const unitPrice = field === 'unitPrice' ? value : item.unitPrice
                newLineItems[index].totalAmount = quantity * unitPrice
            }
        }

        setLineItems(newLineItems)
        setValue('lineItems', newLineItems)
    }

    const calculateSubtotalFromItems = (items: InvoiceLineItem[]) => {
        return items.reduce((sum, item) => sum + (item.totalAmount || 0), 0)
    }

    const calculateSubtotal = () => {
        return calculateSubtotalFromItems(lineItems)
    }

    const calculateTax = () => {
        if (isCustomTax) {
            return customTaxAmount
        }
        const subtotal = calculateSubtotal()
        return subtotal * 0.08 // 8% tax rate
    }

    const calculateTotal = () => {
        if (isCustomTotal) {
            return customTotalAmount
        }
        return calculateSubtotal() + calculateTax()
    }

    const handleZeroOutTax = () => {
        setCustomTaxAmount(0)
        setIsCustomTax(true)
        setValue('customTaxAmount', 0)
    }

    const handleTaxChange = (value: number) => {
        setCustomTaxAmount(value)
        setIsCustomTax(true)
        setValue('customTaxAmount', value)
    }

    const handleTotalChange = (value: number) => {
        setCustomTotalAmount(value)
        setIsCustomTotal(true)
        setValue('customTotalAmount', value)
    }

    const resetToCalculatedTax = () => {
        setIsCustomTax(false)
        const calculatedTax = calculateSubtotal() * 0.08
        setCustomTaxAmount(calculatedTax)
        setValue('customTaxAmount', calculatedTax)
    }

    const resetToCalculatedTotal = () => {
        setIsCustomTotal(false)
        const calculatedTotal = calculateSubtotal() + (isCustomTax ? customTaxAmount : calculateSubtotal() * 0.08)
        setCustomTotalAmount(calculatedTotal)
        setValue('customTotalAmount', calculatedTotal)
    }

    const onSubmit = async (data: FormData) => {
        if (!invoice) return

        try {
            setLoading(true)

            const invoiceData: InvoiceUpdateRequest = {
                organizationId: data.organizationId,
                assemblyLineId: data.assemblyLineId,
                type: data.type,
                status: data.status,
                paymentTerms: data.paymentTerms,
                issueDate: data.issueDate ? data.issueDate.valueOf() : Date.now(),
                billingPeriodStart: data.billingPeriodStart ? data.billingPeriodStart.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                billingPeriodEnd: data.billingPeriodEnd ? data.billingPeriodEnd.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                description: data.description,
                notes: data.notes,
                lineItems: lineItems.filter(item => item.description && item.quantity > 0),
                subtotalAmount: calculateSubtotal(),
                taxAmount: calculateTax(),
                totalAmount: calculateTotal(),
            }

            await clientInvoicesApi.updateInvoice(invoice.id, invoiceData)
            addAlert({ message: 'Invoice updated successfully', type: SnackbarType.Success })
            onSuccess()
        } catch (error) {
            addAlert({ message: 'Failed to update invoice', type: SnackbarType.Error })
            console.error('Error updating invoice:', error)
        } finally {
            setLoading(false)
        }
    }

    if (!invoice) return null

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Edit Invoice - {invoice.invoiceNumber}</DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="organizationId"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Organization ID"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="assemblyLineId"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Assembly Line ID"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="type"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>Invoice Type</InputLabel>
                                        <Select {...field} label="Invoice Type">
                                            <MenuItem value="ONE_TIME">One Time</MenuItem>
                                            <MenuItem value="MONTHLY">Monthly</MenuItem>
                                            <MenuItem value="PRORATED">Prorated</MenuItem>
                                            <MenuItem value="CLOSING">Closing</MenuItem>
                                            <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                                            <MenuItem value="INTERNAL">Internal</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="paymentTerms"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>Payment Terms</InputLabel>
                                        <Select {...field} label="Payment Terms">
                                            <MenuItem value="NET_0">Net 0</MenuItem>
                                            <MenuItem value="NET_7">Net 7</MenuItem>
                                            <MenuItem value="NET_15">Net 15</MenuItem>
                                            <MenuItem value="NET_30">Net 30</MenuItem>
                                            <MenuItem value="NET_60">Net 60</MenuItem>
                                            <MenuItem value="NET_90">Net 90</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="status"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select {...field} label="Status">
                                            <MenuItem value="DRAFT">Draft</MenuItem>
                                            <MenuItem value="PENDING">Pending</MenuItem>
                                            <MenuItem value="SENT">Sent</MenuItem>
                                            <MenuItem value="PAID">Paid</MenuItem>
                                            <MenuItem value="PARTIALLY_PAID">Partially Paid</MenuItem>
                                            <MenuItem value="OVERDUE">Overdue</MenuItem>
                                            <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                            <MenuItem value="REFUNDED">Refunded</MenuItem>
                                            <MenuItem value="ERROR">Error</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="currency"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Currency"
                                        placeholder="USD"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Controller
                                    name="issueDate"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            label="Issue Date"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: !!errors.issueDate,
                                                    helperText: errors.issueDate?.message,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Controller
                                    name="billingPeriodStart"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            label="Billing Period Start"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: !!errors.billingPeriodStart,
                                                    helperText: errors.billingPeriodStart?.message,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Controller
                                    name="billingPeriodEnd"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            label="Billing Period End"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: !!errors.billingPeriodEnd,
                                                    helperText: errors.billingPeriodEnd?.message,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Invoice Description"
                                        multiline
                                        rows={6}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="notes"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Internal Notes"
                                        multiline
                                        rows={3}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Additional Fields */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Additional Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="stripeInvoiceId"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Stripe Invoice ID"
                                        placeholder="in_1234567890"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="stripeCustomerId"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Stripe Customer ID"
                                        placeholder="cus_1234567890"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="externalReference"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="External Reference"
                                        placeholder="Any other external reference"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Line Items */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">Line Items</Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddLineItem}
                                    variant="outlined"
                                    size="small"
                                >
                                    Add Line Item
                                </Button>
                            </Box>
                        </Grid>

                        {lineItems.map((item, index) => {
                            const isSubscription = item.type === 'SUBSCRIPTION'
                            return (
                                <Grid item xs={12} key={index}>
                                    <Box border={1} borderColor="divider" borderRadius={1} p={2}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={2.5}>
                                                <TextField
                                                    fullWidth
                                                    label="Description"
                                                    value={item.description}
                                                    onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Type</InputLabel>
                                                    <Select
                                                        value={item.type || 'CONTRACTOR_HOURS'}
                                                        label="Type"
                                                        onChange={(e) => handleLineItemChange(index, 'type', e.target.value)}
                                                    >
                                                        <MenuItem value="SUBSCRIPTION">Subscription</MenuItem>
                                                        <MenuItem value="CONTRACTOR_HOURS">Hours</MenuItem>
                                                        <MenuItem value="SETUP_FEE">Setup Fee</MenuItem>
                                                        <MenuItem value="OVERAGE">Overage</MenuItem>
                                                        <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                                                        <MenuItem value="DISCOUNT">Discount</MenuItem>
                                                        <MenuItem value="TAX">Tax</MenuItem>
                                                        <MenuItem value="OTHER">Other</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            {!isSubscription && (
                                                <>
                                                    <Grid item xs={6} md={1.5}>
                                                        <TextField
                                                            fullWidth
                                                            label="Quantity"
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} md={2}>
                                                        <TextField
                                                            fullWidth
                                                            label="Unit"
                                                            value={item.unit}
                                                            onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                            {isSubscription && (
                                                <Grid item xs={6} md={3.5}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Period</InputLabel>
                                                        <Select
                                                            value={item.unit || 'MONTHLY'}
                                                            label="Period"
                                                            onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                                                        >
                                                            <MenuItem value="MONTHLY">Monthly</MenuItem>
                                                            <MenuItem value="ANNUAL">Annual</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            )}
                                            <Grid item xs={6} md={2}>
                                                <TextField
                                                    fullWidth
                                                    label="Unit Price"
                                                    type="number"
                                                    value={item.unitPrice}
                                                    onChange={(e) => handleLineItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                    size="small"
                                                    InputProps={{
                                                        startAdornment: <Typography variant="caption">$</Typography>,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <TextField
                                                    fullWidth
                                                    label="Total Amount"
                                                    value={`$${item.totalAmount?.toFixed(2) || '0.00'}`}
                                                    size="small"
                                                    InputProps={{
                                                        readOnly: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={1}>
                                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                                    <IconButton
                                                        onClick={() => handleRemoveLineItem(index)}
                                                        disabled={lineItems.length === 1}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            )
                        })}

                        {/* Totals */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Box display="flex" justifyContent="flex-end">
                                <Box width="400px">
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography>Subtotal:</Typography>
                                        <Typography>${calculateSubtotal().toFixed(2)}</Typography>
                                    </Box>

                                    {/* Tax Section with Edit Capability */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography>Tax:</Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {isCustomTax ? (
                                                <>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={customTaxAmount}
                                                        onChange={(e) => handleTaxChange(parseFloat(e.target.value) || 0)}
                                                        InputProps={{
                                                            startAdornment: <Typography variant="caption">$</Typography>,
                                                        }}
                                                        sx={{ width: '80px' }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        onClick={resetToCalculatedTax}
                                                        variant="text"
                                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                                    >
                                                        Reset
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography>${calculateTax().toFixed(2)}</Typography>
                                                    <Button
                                                        size="small"
                                                        onClick={() => setIsCustomTax(true)}
                                                        variant="text"
                                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </>
                                            )}
                                            <Button
                                                size="small"
                                                onClick={handleZeroOutTax}
                                                variant="outlined"
                                                color="secondary"
                                                sx={{ minWidth: 'auto', p: 0.5 }}
                                            >
                                                Zero
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 1 }} />

                                    {/* Total Section with Edit Capability */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="h6">Total:</Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {isCustomTotal ? (
                                                <>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={customTotalAmount}
                                                        onChange={(e) => handleTotalChange(parseFloat(e.target.value) || 0)}
                                                        InputProps={{
                                                            startAdornment: <Typography variant="caption">$</Typography>,
                                                        }}
                                                        sx={{ width: '100px' }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        onClick={resetToCalculatedTotal}
                                                        variant="text"
                                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                                    >
                                                        Reset
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="h6">${calculateTotal().toFixed(2)}</Typography>
                                                    <Button
                                                        size="small"
                                                        onClick={() => setIsCustomTotal(true)}
                                                        variant="text"
                                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? 'Updating...' : 'Update Invoice'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
